import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import Lenis from "lenis";
import { toast } from "sonner";
import { ArrowUpRight, Phone, ArrowRight, ShieldCheck, ScrollText, Scale, Quote } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import { Reveal, LineReveal } from "@/components/site/Reveal";
import { PhoneLink } from "@/components/site/PhoneLink";
import { BRAND, IMG } from "@/lib/brand";
import { api, apiError } from "@/lib/api";

const SERVICES = [
  {
    key: "credit",
    n: "01",
    title: "Credit Recovery",
    img: IMG.credit,
    icon: ShieldCheck,
    span: "md:col-span-7",
    body: "Practical, compliant recovery of overdue and bad-debt accounts. We pursue what you are owed with discretion and persistence — so you can stay focused on your business.",
  },
  {
    key: "serving",
    n: "02",
    title: "Document Serving",
    img: IMG.serving,
    icon: ScrollText,
    span: "md:col-span-5",
    body: "Professional service of demands and District Court documents, nationwide, with sworn affidavits of service.",
  },
  {
    key: "debt",
    n: "03",
    title: "Debt Management",
    img: IMG.debt,
    icon: Scale,
    span: "md:col-span-12",
    body: "Structured management of complex credit and bad-debt portfolios, from first demand through to enforceable judgment — measured, methodical and results-focused.",
  },
];

const STEPS = [
  { n: "01", t: "Discuss", d: "A free, no-obligation initial discussion to understand your situation and the overdue accounts you need recovered." },
  { n: "02", t: "Negotiate", d: "Our team moves quickly to resolve the matter, using refined negotiation techniques honed over years of experience." },
  { n: "03", t: "Instruct", d: "You refer your overdue accounts. We review the file, confirm the position and agree a clear course of action." },
  { n: "04", t: "Demand", d: "A formal, legally compliant demand is issued — firm, professional and designed to prompt payment or arrangement." },
  { n: "05", t: "Recover", d: "Where needed, we serve documents and pursue District Court proceedings, keeping you informed at every step." },
  { n: "06", t: "Resolve", d: "Funds are recovered and remitted, or judgment is secured and enforced. You stay focused on running your business." },
];

const RIBBON = [
  "Authorised FEDS Network Agent",
  "30+ Years Experience",
  "Discreet",
  "Professional",
  "Results-Focused",
  "Nationwide Coverage",
];

export default function Home() {
  const [siteImg, setSiteImg] = useState(IMG);
  const [testimonials, setTestimonials] = useState([]);
  const heroRef = useRef(null);

  useEffect(() => {
    api.get("/settings/images").then((r) => setSiteImg({ ...IMG, ...r.data.images })).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data.testimonials || [])).catch(() => {});
  }, []);

  // Inject machine-readable review structured data (schema.org) for search engines
  useEffect(() => {
    if (!testimonials.length) return;
    const data = {
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      "@id": "https://grafton-recovery.emergent.host/#business",
      name: "Grafton Group",
      review: testimonials.map((t) => ({
        "@type": "Review",
        reviewBody: t.quote,
        author: { "@type": "Organization", name: t.role ? `${t.author} — ${t.role}` : t.author },
      })),
    };
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-testid", "reviews-jsonld");
    el.text = JSON.stringify(data);
    document.head.appendChild(el);
    return () => { document.head.removeChild(el); };
  }, [testimonials]);

  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const overlayText = useTransform(scrollYProgress, [0, 1], [0, -80]);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", service: "Credit Recovery", amount: "", message: "", reason: "Contact", reference: "", terms_accepted: false });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.terms_accepted) {
      toast.error("Please confirm you have read the Terms of Engagement & Schedule of Costs.");
      return;
    }
    setSending(true);
    try {
      await api.post("/quotes", form);
      toast.success("Enquiry received. We'll be in touch shortly.");
      setForm({ name: "", company: "", email: "", phone: "", service: "Credit Recovery", amount: "", message: "", reason: "Contact", reference: "", terms_accepted: false });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-[#0A0D0B]">
      <Nav />

      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[100svh] overflow-hidden">
        <motion.div className="absolute inset-0 z-0" style={{ y: bgY }}>
          <img src={siteImg.hero} alt="" className="w-full h-[120%] object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D0B] via-[#0A0D0B]/55 to-[#0A0D0B]/70" />
          <div className="absolute inset-0 bg-[#0A0D0B]/25" />
        </motion.div>

        <motion.div
          style={{ y: overlayText }}
          className="relative z-10 min-h-[100svh] max-w-[1400px] mx-auto px-5 md:px-10 flex flex-col justify-end pt-28 md:pt-0 pb-16 md:pb-24"
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 1 }}
            className="mb-6 flex items-center gap-3 text-[#B89874]"
          >
            <span className="h-px w-10 bg-[#B89874]" />
            <span className="text-[11px] uppercase tracking-[0.3em] font-medium">{BRAND.positioning}</span>
          </motion.div>

          <h1 className="font-display text-[#F5F3EC] leading-[1.08] sm:leading-[0.98] tracking-tight text-[10.5vw] sm:text-6xl md:text-7xl lg:text-[88px] max-w-5xl">
            <LineReveal delay={0.35}>Credit Recovery</LineReveal>
            <LineReveal delay={0.5}>
              &amp; Document Serving
            </LineReveal>
            <LineReveal delay={0.65}>
              <span className="italic text-[#B89874]">Specialists</span>
            </LineReveal>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.9 }}
            className="mt-7 text-[#A3ACA6] text-base md:text-lg max-w-xl leading-relaxed"
          >
            Professional, discreet and results-focused debt recovery and process serving across New Zealand.
            An Authorised FEDS Network Agent.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.9 }}
            className="mt-9 flex flex-col sm:flex-row gap-4"
          >
            <a
              href="#contact"
              data-testid="hero-quote-btn"
              className="group inline-flex items-center justify-center gap-2 bg-[#B89874] text-[#0A0D0B] px-8 py-4 text-sm font-semibold uppercase tracking-[0.12em] hover:bg-[#C9A780] transition-colors duration-300"
            >
              Contact Us
              <ArrowUpRight size={17} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
            </a>
            <a
              href={BRAND.phoneHref}
              data-testid="hero-call-btn"
              className="inline-flex items-center justify-center gap-2 border border-[#233027] text-[#F5F3EC] px-8 py-4 text-sm font-semibold uppercase tracking-[0.12em] hover:border-[#B89874] hover:text-[#B89874] transition-colors duration-300"
            >
              <Phone size={16} /> Call {BRAND.phone}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 1 }}
            className="mt-10 font-display italic text-[#B89874] text-xl md:text-2xl"
          >
            “{BRAND.tagline}”
          </motion.div>
        </motion.div>
      </section>

      {/* TRUST RIBBON */}
      <div className="bg-[#1B3022] border-y border-[#233027] py-5">
        <Marquee speed={38} gradient={false} autoFill>
          {RIBBON.map((r, i) => (
            <span key={i} className="flex items-center text-[#F5F3EC]">
              <span className="mx-8 text-sm md:text-base uppercase tracking-[0.22em] font-medium whitespace-nowrap">{r}</span>
              <span className="text-[#B89874]">✦</span>
            </span>
          ))}
        </Marquee>
      </div>

      {/* SERVICES */}
      <section id="services" className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-36">
        <Reveal className="max-w-3xl mb-14 md:mb-20">
          <span className="text-[11px] uppercase tracking-[0.3em] text-[#B89874]">What we do</span>
          <h2 className="font-display text-4xl md:text-6xl tracking-tight text-[#F5F3EC] mt-4 leading-[1.02]">
            Three disciplines,<br /><span className="italic text-[#A3ACA6]">one outcome.</span>
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <Reveal key={s.key} delay={i * 0.1} className={`${s.span} group`}>
                <div className="relative h-[420px] md:h-[460px] overflow-hidden border border-[#233027] bg-[#121814]">
                  <img
                    src={siteImg[s.key]}
                    alt={s.title}
                    className="absolute inset-0 w-full h-full object-cover opacity-45 group-hover:opacity-60 group-hover:scale-105 transition-[opacity,transform] duration-700 ease-out"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D0B] via-[#0A0D0B]/30 to-transparent" />
                  <div className="relative h-full p-8 md:p-10 flex flex-col justify-between">
                    <div className="flex items-start justify-between">
                      <span className="font-mono-gg text-[#B89874] text-sm">{s.n}</span>
                      <Icon size={26} className="text-[#B89874]" strokeWidth={1.4} />
                    </div>
                    <div>
                      <h3 className="font-display text-3xl md:text-4xl text-[#F5F3EC] tracking-tight">{s.title}</h3>
                      <p className="mt-3 text-[#A3ACA6] max-w-md leading-relaxed">{s.body}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* PROCESS */}
      <section id="process" className="border-y border-[#233027] bg-[#0C110E]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-36">
          <Reveal className="mb-16">
            <span className="text-[11px] uppercase tracking-[0.3em] text-[#B89874]">How we work</span>
            <h2 className="font-display text-4xl md:text-6xl tracking-tight text-[#F5F3EC] mt-4">
              A measured, six-step method.
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-x-16 gap-y-12">
            {STEPS.map((st, i) => (
              <Reveal key={st.n} delay={i * 0.08}>
                <div className="flex gap-6 md:gap-8 items-start border-t border-[#233027] pt-8">
                  <span className="font-display text-5xl md:text-7xl text-[#233027] leading-none tabnum">{st.n}</span>
                  <div>
                    <h3 className="font-display text-2xl md:text-3xl text-[#F5F3EC]">{st.t}</h3>
                    <p className="mt-3 text-[#A3ACA6] leading-relaxed max-w-md">{st.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-36">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-center">
          <Reveal className="md:col-span-5">
            <div className="relative">
              <div className="absolute -inset-3 border border-[#233027]" />
              <img src={siteImg.portrait} alt="Paul Kyle" className="relative w-full object-cover aspect-[3/4] grayscale-[15%]" />
              <div className="absolute bottom-5 left-5 bg-[#0A0D0B]/85 backdrop-blur px-5 py-3 border-l-2 border-[#B89874]">
                <div className="font-display text-xl text-[#F5F3EC]">Paul Kyle</div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#B89874]">Principal</div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.12} className="md:col-span-7">
            <span className="text-[11px] uppercase tracking-[0.3em] text-[#B89874]">Experience</span>
            <h2 className="font-display text-3xl md:text-5xl tracking-tight text-[#F5F3EC] mt-4 leading-[1.08]">
              Over <span className="italic text-[#B89874]">30 years</span> in credit, recovery & financial services.
            </h2>
            <div className="mt-7 space-y-5 text-[#A3ACA6] leading-relaxed text-[15px] md:text-base max-w-2xl">
              <p>
                Paul Kyle brings more than three decades of experience in credit management, financial services and recovery
                work. His background spans senior roles in banking, full P&amp;L responsibility as a McDonald’s franchisee,
                executive management in education, and over a decade managing complex credit and bad-debt portfolios.
              </p>
              <p>
                He specialises in practical, compliant debt recovery and process serving, and is an authorised agent of the
                FEDS Network. Grafton Group focuses on delivering clear results — while allowing clients to stay focused on
                running their own businesses.
              </p>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-4">
              {[["30+", "Years experience"], ["NZ", "Nationwide coverage"], ["FEDS", "Authorised agent"]].map(([a, b]) => (
                <div key={b} className="border-t border-[#233027] pt-4">
                  <div className="font-display text-3xl md:text-4xl text-[#F5F3EC] tabnum">{a}</div>
                  <div className="text-xs uppercase tracking-[0.14em] text-[#58635C] mt-1">{b}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section id="testimonials" className="border-t border-[#233027] bg-[#0C110E]">
          <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-36">
            <Reveal className="max-w-3xl mb-14 md:mb-20">
              <span className="text-[11px] uppercase tracking-[0.3em] text-[#B89874]">In their words</span>
              <h2 className="font-display text-4xl md:text-6xl tracking-tight text-[#F5F3EC] mt-4 leading-[1.02]">
                Trusted by New Zealand<br /><span className="italic text-[#A3ACA6]">businesses.</span>
              </h2>
            </Reveal>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="testimonials-list">
              {testimonials.map((t, i) => (
                <Reveal key={t.id || i} delay={i * 0.1}>
                  <figure
                    data-testid={`testimonial-${i}`}
                    className="h-full flex flex-col border border-[#233027] bg-[#121814] p-8 md:p-9 hover:border-[#B89874]/60 transition-colors duration-500"
                  >
                    <Quote size={30} className="text-[#B89874] shrink-0" strokeWidth={1.4} />
                    <blockquote className="mt-5 text-[#D6DAD5] leading-relaxed text-[15px] flex-1">
                      “{t.quote}”
                    </blockquote>
                    <figcaption className="mt-7 pt-6 border-t border-[#233027]">
                      <div className="font-display text-lg text-[#F5F3EC]">{t.author}</div>
                      {t.role && <div className="text-[11px] uppercase tracking-[0.16em] text-[#B89874] mt-1">{t.role}</div>}
                    </figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CONTACT */}
      <section id="contact" className="border-t border-[#233027] bg-[#0C110E]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-36 grid md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-5">
            <span className="text-[11px] uppercase tracking-[0.3em] text-[#B89874]">Get in touch</span>
            <h2 className="font-display text-4xl md:text-6xl tracking-tight text-[#F5F3EC] mt-4 leading-[1.02]">
              Owed money?<br /><span className="italic text-[#B89874]">Leave it to us.</span>
            </h2>
            <p className="mt-6 text-[#A3ACA6] leading-relaxed max-w-md">
              Request a confidential, no-obligation quote. We respond promptly and discreetly.
            </p>
            <div className="mt-10 space-y-4">
              <PhoneLink className="flex items-center gap-4 text-[#F5F3EC] hover:text-[#B89874] transition-colors">
                <Phone size={18} className="text-[#B89874]" /> {BRAND.phone}
              </PhoneLink>
              <a href={`mailto:${BRAND.email}`} className="flex items-center gap-4 text-[#F5F3EC] hover:text-[#B89874] transition-colors break-all">
                <ArrowRight size={18} className="text-[#B89874]" /> {BRAND.email}
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="md:col-span-7">
            <form onSubmit={submit} data-testid="quote-form" className="bg-[#121814] border border-[#233027] p-6 md:p-9">
              <div className="flex flex-col gap-2 mb-4">
                <label className="text-[11px] uppercase tracking-[0.18em] text-[#A3ACA6]">I'd like to <span className="text-[#B89874]">*</span></label>
                <div className="grid grid-cols-3 gap-2">
                  {["Contact", "Quote", "Debtor reply"].map((r) => (
                    <button
                      key={r}
                      type="button"
                      data-testid={`q-reason-${r.replace(/\s+/g, "-").toLowerCase()}`}
                      onClick={() => setForm((f) => ({ ...f, reason: r }))}
                      className={`px-3 py-3 text-xs font-semibold uppercase tracking-[0.08em] border transition-colors ${form.reason === r ? "bg-[#B89874] text-[#0A0D0B] border-[#B89874]" : "bg-[#0A0D0B] text-[#A3ACA6] border-[#233027] hover:border-[#B89874]"}`}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Full name" testid="q-name" value={form.name} onChange={set("name")} required />
                <Field label="Company" testid="q-company" value={form.company} onChange={set("company")} />
                <Field label="Email" testid="q-email" type="email" value={form.email} onChange={set("email")} required />
                <Field label="Phone" testid="q-phone" value={form.phone} onChange={set("phone")} />
                {form.reason === "Debtor reply" ? (
                  <Field label="Reference (GG-… or invoice)" testid="q-reference" value={form.reference} onChange={set("reference")} required />
                ) : (
                  <div className="flex flex-col gap-2">
                    <label className="text-[11px] uppercase tracking-[0.18em] text-[#A3ACA6]">Service</label>
                    <select
                      data-testid="q-service"
                      value={form.service}
                      onChange={set("service")}
                      className="bg-[#0A0D0B] border border-[#233027] px-4 py-3 text-[#F5F3EC] focus:border-[#B89874] outline-none transition-colors"
                    >
                      <option>Credit Recovery</option>
                      <option>Document Serving</option>
                      <option>Debt Management</option>
                      <option>General enquiry</option>
                    </select>
                  </div>
                )}
                <Field label="Approx. amount owed" testid="q-amount" value={form.amount} onChange={set("amount")} />
              </div>
              <div className="flex flex-col gap-2 mt-4">
                <label className="text-[11px] uppercase tracking-[0.18em] text-[#A3ACA6]">Message</label>
                <textarea
                  data-testid="q-message"
                  rows={4}
                  value={form.message}
                  onChange={set("message")}
                  className="bg-[#0A0D0B] border border-[#233027] px-4 py-3 text-[#F5F3EC] focus:border-[#B89874] outline-none transition-colors resize-none"
                />
              </div>
              <label className="flex items-start gap-3 mt-5 cursor-pointer" data-testid="q-terms-label">
                <input
                  type="checkbox"
                  data-testid="q-terms"
                  checked={form.terms_accepted}
                  onChange={(e) => setForm((f) => ({ ...f, terms_accepted: e.target.checked }))}
                  className="mt-1 h-4 w-4 accent-[#B89874] shrink-0"
                />
                <span className="text-xs text-[#A3ACA6] leading-relaxed">
                  I confirm I have read the{" "}
                  <Link to="/terms" target="_blank" className="text-[#B89874] underline hover:text-[#C9A780]">Terms of Engagement</Link>{" "}and{" "}
                  <Link to="/schedule" target="_blank" className="text-[#B89874] underline hover:text-[#C9A780]">Schedule of Costs</Link>.
                  <span className="text-[#B89874]"> *</span>
                </span>
              </label>
              <button
                type="submit"
                disabled={sending}
                data-testid="quote-submit"
                className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#B89874] text-[#0A0D0B] px-8 py-4 text-sm font-semibold uppercase tracking-[0.12em] hover:bg-[#C9A780] transition-colors disabled:opacity-60"
              >
                {sending ? "Sending…" : form.reason === "Quote" ? "Request Quote" : form.reason === "Debtor reply" ? "Send Reply" : "Send Enquiry"} <ArrowRight size={16} />
              </button>
            </form>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function Field({ label, testid, type = "text", value, onChange, required }) {
  return (
    <div className="flex flex-col gap-2">
      <label className="text-[11px] uppercase tracking-[0.18em] text-[#A3ACA6]">
        {label}{required && <span className="text-[#B89874]"> *</span>}
      </label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        className="bg-[#0A0D0B] border border-[#233027] px-4 py-3 text-[#F5F3EC] focus:border-[#B89874] outline-none transition-colors"
      />
    </div>
  );
}
